import { useEffect, useState } from "react";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";

interface IngestStatus {
  ingestRoot: string;
  mounted: boolean;
  topLevelEntries: number;
  latestRun: {
    id: number;
    startedAt: string;
    finishedAt: string | null;
    status: "running" | "done" | "failed";
    filesSeen: number;
    filesImported: number;
    filesDuplicate: number;
    filesUnsupported: number;
    filesJunk: number;
    filesFailed: number;
    bytesImported: number;
    bytesDuplicate: number;
    bytesJunk: number;
    error: string | null;
  } | null;
  pendingByKind: Array<{ kind: string; n: number; bytes: number | string }>;
}

interface Finding {
  id: number;
  ingestPath: string;
  sizeBytes: number;
  kind: "duplicate" | "unsupported" | "junk" | "failed";
  reason: string | null;
  existingMediaId: number | null;
  existingRelativePath: string | null;
  status: "pending" | "resolved";
  discoveredAt: string;
  resolution: "kept" | "deleted" | "manual" | null;
}

async function getJson<T>(url: string): Promise<T> {
  const r = await fetch(url, { credentials: "include" });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return r.json();
}

async function postJson<T>(url: string, body: unknown = {}): Promise<T> {
  const r = await fetch(url, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return r.json();
}

const KIND_COLORS: Record<string, string> = {
  duplicate: "bg-amber-700/40 text-amber-100",
  unsupported: "bg-zinc-700 text-zinc-100",
  junk: "bg-rose-800/40 text-rose-100",
  failed: "bg-red-700/40 text-red-100",
};

type SortMode = "size_desc" | "size_asc" | "recent";

interface BreakdownEntry {
  ext: string;
  n: number;
  bytes: string | number;
}

export function IngestPanel() {
  const qc = useQueryClient();
  const [filterKind, setFilterKind] = useState<"duplicate" | "junk" | "unsupported" | "failed">("duplicate");
  const [filterStatus, setFilterStatus] = useState<"pending" | "resolved">("pending");
  const [sort, setSort] = useState<SortMode>("size_desc");
  const [showBreakdown, setShowBreakdown] = useState(false);
  // search input vs the *committed* query — debounce on Enter / blur so we
  // don't refire infinite queries on every keystroke.
  const [searchDraft, setSearchDraft] = useState("");
  const [search, setSearch] = useState("");

  const status = useQuery<IngestStatus>({
    queryKey: ["ingest", "status"],
    queryFn: () => getJson<IngestStatus>("/api/admin/ingest/status"),
    refetchInterval: 4000,
  });

  const qParam = search ? `&q=${encodeURIComponent(search)}` : "";

  const PAGE_SIZE = 200;
  const findings = useInfiniteQuery({
    queryKey: ["ingest", "findings", filterKind, filterStatus, sort, search],
    queryFn: ({ pageParam = 0 }) =>
      getJson<{ items: Finding[]; total: number; offset: number; limit: number }>(
        `/api/admin/ingest/findings?kind=${filterKind}&status=${filterStatus}&sort=${sort}&limit=${PAGE_SIZE}&offset=${pageParam}${qParam}`,
      ),
    initialPageParam: 0,
    getNextPageParam: (last) => {
      const nextOffset = last.offset + last.items.length;
      return nextOffset < last.total ? nextOffset : undefined;
    },
  });
  const findingsItems: Finding[] = findings.data?.pages.flatMap((p) => p.items) ?? [];
  const findingsTotal = findings.data?.pages[0]?.total ?? 0;

  const breakdown = useQuery({
    queryKey: ["ingest", "breakdown", filterKind, filterStatus, search],
    queryFn: () =>
      getJson<{ extensions: BreakdownEntry[] }>(
        `/api/admin/ingest/findings/breakdown?kind=${filterKind}&status=${filterStatus}${qParam}`,
      ),
    enabled: showBreakdown,
  });

  const runIngest = useMutation({
    mutationFn: () => postJson<{ ok: true }>("/api/admin/ingest/run"),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ingest"] }),
  });

  const reclassify = useMutation({
    mutationFn: () => api.reclassifyUnsupported(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ingest"] }),
  });

  const resolveOne = useMutation({
    mutationFn: ({ id, action }: { id: number; action: "delete" | "keep" }) =>
      postJson<{ ok: true }>(`/api/admin/ingest/findings/${id}/resolve`, { action }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ingest"] }),
  });

  const bulkResolve = useMutation({
    mutationFn: ({
      kind,
      action,
      q,
      ids,
    }: {
      kind?: string;
      action: "delete" | "keep";
      q?: string;
      ids?: number[];
    }) =>
      postJson<{ ok: true; processed: number }>(`/api/admin/ingest/findings/bulk-resolve`, {
        action,
        ...(kind ? { kind } : {}),
        ...(q ? { q } : {}),
        ...(ids ? { ids } : {}),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ingest"] }),
  });

  // Per-row checkbox selection — local to this panel, cleared when filters
  // change so we don't accidentally act on rows the user can no longer see.
  const [picked, setPicked] = useState<Set<number>>(new Set());
  useEffect(() => {
    setPicked(new Set());
  }, [filterKind, filterStatus, sort, search]);

  const s = status.data;

  return (
    <div className="rounded border border-zinc-800 bg-zinc-900 p-4">
      <h3 className="mb-2 text-base font-medium">Ingest (legacy library migration)</h3>
      <p className="mb-3 text-xs text-zinc-500">
        Mount your old library at <code>{s?.ingestRoot ?? "/ingest"}</code>. The walker classifies
        each file (junk, unsupported, duplicate, or new media), moves new media into{" "}
        <code>/media/photos|videos/YYYY/MM/</code>, and queues everything else for your review here.
      </p>

      {!s ? (
        <div className="text-xs text-zinc-500">Loading status…</div>
      ) : !s.mounted ? (
        <div className="rounded bg-zinc-800/60 px-3 py-2 text-xs text-zinc-300">
          Nothing mounted at <code>{s.ingestRoot}</code>. Mount your old library there to begin.
        </div>
      ) : (
        <>
          <div className="mb-3 flex flex-wrap items-center gap-3 text-xs">
            <div className="rounded bg-zinc-800 px-2 py-1 text-zinc-300">
              {s.topLevelEntries} top-level {s.topLevelEntries === 1 ? "entry" : "entries"} mounted
            </div>
            <button
              onClick={() => runIngest.mutate()}
              disabled={runIngest.isPending || s.latestRun?.status === "running"}
              className="rounded bg-emerald-700 px-3 py-1 text-emerald-100 hover:bg-emerald-600 disabled:opacity-50"
            >
              {s.latestRun?.status === "running" ? "Running…" : "Start ingest"}
            </button>
            <button
              onClick={async () => {
                if (
                  !window.confirm(
                    "Reset every resolved-as-unsupported finding whose extension is now in the supported list back to pending? Run a fresh ingest afterwards to actually re-process them.",
                  )
                ) {
                  return;
                }
                const r = await reclassify.mutateAsync();
                window.alert(`Re-pended ${r.restored} of ${r.scanned} resolved-unsupported findings.`);
              }}
              disabled={reclassify.isPending}
              className="rounded bg-zinc-700 px-3 py-1 text-zinc-100 hover:bg-zinc-600 disabled:opacity-50"
              title="Move resolved-unsupported findings whose extension is now whitelisted back to pending so the next run picks them up. Useful after .flv / .avc / etc. were added to the supported set."
            >
              ↺ Re-classify legacy unsupported
            </button>
            {s.latestRun && (
              <span className="text-zinc-500">
                latest: {s.latestRun.status} ·{" "}
                {s.latestRun.filesSeen} seen · {s.latestRun.filesImported} imported ·{" "}
                {s.latestRun.filesDuplicate} duplicates · {s.latestRun.filesJunk} junk ·{" "}
                {s.latestRun.filesUnsupported} unsupported · {s.latestRun.filesFailed} failed
              </span>
            )}
          </div>

          {/* Pending counts + bulk resolve */}
          {s.pendingByKind.length > 0 && (
            <div className="mb-4 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
              {s.pendingByKind.map((row) => (
                <div
                  key={row.kind}
                  className="flex items-center justify-between rounded bg-zinc-800/60 px-3 py-2 text-xs"
                >
                  <div>
                    <span
                      className={
                        "inline-block rounded px-1.5 py-0.5 text-[10px] uppercase " +
                        (KIND_COLORS[row.kind] ?? "bg-zinc-700")
                      }
                    >
                      {row.kind}
                    </span>
                    <span className="ml-2 text-zinc-300">{row.n} pending</span>
                    <span className="ml-2 text-zinc-500">{formatBytes(Number(row.bytes))}</span>
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={() => {
                        bulkResolve.mutate({ kind: row.kind, action: "keep" });
                      }}
                      disabled={bulkResolve.isPending}
                      className="rounded bg-zinc-700 px-2 py-0.5 text-zinc-100 hover:bg-zinc-600 disabled:opacity-50"
                      title="Mark all resolved without acting on the source files. Re-running ingest will re-discover them."
                    >
                      Keep all
                    </button>
                    <button
                      onClick={() => {
                        if (
                          window.confirm(
                            `Move all ${row.n} ${row.kind} entries to trash? Files go to /media/.media-viewer/trash/ingest-deleted/ and can be restored from /admin/trash.`,
                          )
                        ) {
                          bulkResolve.mutate({ kind: row.kind, action: "delete" });
                        }
                      }}
                      disabled={bulkResolve.isPending}
                      className="rounded bg-rose-800/60 px-2 py-0.5 text-rose-100 hover:bg-rose-800 disabled:opacity-50"
                    >
                      Delete all
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Search bar — supports glob (* and ?) or substring match */}
          <div className="mb-2 flex flex-wrap items-center gap-2 text-xs">
            <label className="text-zinc-500">Search path:</label>
            <input
              value={searchDraft}
              onChange={(e) => setSearchDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") setSearch(searchDraft.trim());
                else if (e.key === "Escape") {
                  setSearchDraft("");
                  setSearch("");
                }
              }}
              onBlur={() => setSearch(searchDraft.trim())}
              placeholder="e.g. *.mp4.jpg  or  thumbs/  (Enter to apply)"
              className="min-w-[260px] flex-1 rounded border border-zinc-700 bg-zinc-800 px-2 py-1 font-mono"
            />
            {search && (
              <>
                <button
                  onClick={() => {
                    setSearchDraft("");
                    setSearch("");
                  }}
                  className="rounded bg-zinc-800 px-2 py-1 hover:bg-zinc-700"
                >
                  Clear
                </button>
                <button
                  onClick={() =>
                    bulkResolve.mutate({ kind: filterKind, action: "keep", q: search })
                  }
                  disabled={bulkResolve.isPending || findingsTotal === 0}
                  className="rounded bg-zinc-700 px-2 py-1 hover:bg-zinc-600 disabled:opacity-50"
                  title="Mark all matching as resolved without touching the files (so they can be reprocessed by a future ingest run)"
                >
                  Keep all {findingsTotal.toLocaleString()} matching
                </button>
                <button
                  onClick={() => {
                    if (
                      window.confirm(
                        `Delete-source on all ${findingsTotal.toLocaleString()} ${filterKind} findings matching "${search}"? Files go to /media/.media-viewer/trash/ingest-deleted/.`,
                      )
                    ) {
                      bulkResolve.mutate({ kind: filterKind, action: "delete", q: search });
                    }
                  }}
                  disabled={bulkResolve.isPending || findingsTotal === 0}
                  className="rounded bg-rose-800/60 px-2 py-1 text-rose-100 hover:bg-rose-800 disabled:opacity-50"
                  title="Bulk-delete all findings matching the search"
                >
                  🗑 Delete all {findingsTotal.toLocaleString()} matching
                </button>
              </>
            )}
          </div>

          {/* Findings table */}
          <div className="mb-2 flex flex-wrap items-center gap-2 text-xs">
            <label className="text-zinc-500">Kind:</label>
            <select
              value={filterKind}
              onChange={(e) => setFilterKind(e.target.value as typeof filterKind)}
              className="rounded border border-zinc-700 bg-zinc-800 px-2 py-1"
            >
              <option value="duplicate">duplicate</option>
              <option value="junk">junk</option>
              <option value="unsupported">unsupported</option>
              <option value="failed">failed</option>
            </select>
            <label className="text-zinc-500">Status:</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as typeof filterStatus)}
              className="rounded border border-zinc-700 bg-zinc-800 px-2 py-1"
            >
              <option value="pending">pending</option>
              <option value="resolved">resolved</option>
            </select>
            <label className="text-zinc-500">Sort:</label>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value as SortMode)}
              className="rounded border border-zinc-700 bg-zinc-800 px-2 py-1"
            >
              <option value="size_desc">largest first</option>
              <option value="size_asc">smallest first</option>
              <option value="recent">most recent</option>
            </select>
            <button
              onClick={() => setShowBreakdown((v) => !v)}
              className={
                "ml-auto rounded px-2 py-1 " +
                (showBreakdown
                  ? "bg-emerald-700 text-emerald-100"
                  : "bg-zinc-800 hover:bg-zinc-700")
              }
              title="Group by file extension"
            >
              {showBreakdown ? "Hide breakdown" : "Show by extension"}
            </button>
          </div>

          {/* Extension breakdown — only shown above the table when toggled */}
          {showBreakdown && (
            <div className="mb-3 rounded border border-zinc-800 bg-zinc-900/60 p-3">
              <div className="mb-1 text-[10px] uppercase tracking-wider text-zinc-500">
                {filterStatus} {filterKind} — by extension (top 50 by total size)
              </div>
              {breakdown.isPending ? (
                <div className="text-xs text-zinc-500">Loading…</div>
              ) : breakdown.isError ? (
                <div className="text-xs text-red-300">Error: {String(breakdown.error)}</div>
              ) : breakdown.data.extensions.length === 0 ? (
                <div className="text-xs text-zinc-500">Nothing to summarize.</div>
              ) : (
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-zinc-500">
                      <th className="px-2 py-1">Ext</th>
                      <th className="px-2 py-1 text-right">Files</th>
                      <th className="px-2 py-1 text-right">Total size</th>
                      <th className="px-2 py-1 text-right">Avg / file</th>
                    </tr>
                  </thead>
                  <tbody>
                    {breakdown.data.extensions.map((e) => {
                      const bytes = Number(e.bytes);
                      const avg = e.n > 0 ? bytes / e.n : 0;
                      return (
                        <tr key={e.ext} className="border-t border-zinc-800/60">
                          <td className="px-2 py-1 font-mono text-zinc-300">.{e.ext}</td>
                          <td className="px-2 py-1 text-right text-zinc-400">{e.n.toLocaleString()}</td>
                          <td className="px-2 py-1 text-right text-zinc-300">{formatBytes(bytes)}</td>
                          <td className="px-2 py-1 text-right text-zinc-500">{formatBytes(avg)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {findings.isPending ? (
            <div className="text-xs text-zinc-500">Loading findings…</div>
          ) : findings.isError ? (
            <div className="text-xs text-red-300">Error: {String(findings.error)}</div>
          ) : findingsItems.length === 0 ? (
            <div className="rounded bg-zinc-800/40 px-3 py-2 text-xs text-zinc-400">
              No {filterStatus} {filterKind} findings.
            </div>
          ) : (
            <>
              <div className="mb-1 flex flex-wrap items-center gap-3 text-[10px] uppercase tracking-wider text-zinc-500">
                <span>
                  Showing {findingsItems.length.toLocaleString()} of {findingsTotal.toLocaleString()}
                </span>
                {picked.size > 0 && (
                  <span className="rounded bg-emerald-700/40 px-2 py-0.5 normal-case text-emerald-100">
                    {picked.size.toLocaleString()} picked
                  </span>
                )}
              </div>

              {/* Selection action bar — only visible when at least one row is checked */}
              {picked.size > 0 && filterStatus === "pending" && (
                <div className="mb-2 flex flex-wrap items-center gap-2 rounded border border-emerald-700/40 bg-emerald-900/20 px-3 py-2 text-xs">
                  <span className="text-zinc-300">
                    {picked.size.toLocaleString()} finding{picked.size === 1 ? "" : "s"} selected
                  </span>
                  <button
                    onClick={() => {
                      const ids = Array.from(picked);
                      if (
                        window.confirm(
                          `Delete-source on ${ids.length.toLocaleString()} finding${ids.length === 1 ? "" : "s"}? Files move to /media/.media-viewer/trash/ingest-deleted/.`,
                        )
                      ) {
                        bulkResolve.mutate(
                          { action: "delete", ids },
                          { onSuccess: () => setPicked(new Set()) },
                        );
                      }
                    }}
                    disabled={bulkResolve.isPending}
                    className="rounded bg-rose-800/60 px-2 py-1 text-rose-100 hover:bg-rose-800 disabled:opacity-50"
                  >
                    🗑 Delete selected
                  </button>
                  <button
                    onClick={() => {
                      const ids = Array.from(picked);
                      bulkResolve.mutate(
                        { action: "keep", ids },
                        { onSuccess: () => setPicked(new Set()) },
                      );
                    }}
                    disabled={bulkResolve.isPending}
                    className="rounded bg-zinc-700 px-2 py-1 hover:bg-zinc-600 disabled:opacity-50"
                  >
                    Keep selected
                  </button>
                  <button
                    onClick={() => setPicked(new Set())}
                    className="rounded bg-zinc-800 px-2 py-1 text-zinc-300 hover:bg-zinc-700"
                  >
                    Clear
                  </button>
                </div>
              )}

              <div className="max-h-[60vh] overflow-auto rounded border border-zinc-800">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-zinc-900 text-left text-zinc-500">
                    <tr>
                      <th className="w-6 px-2 py-1">
                        <input
                          type="checkbox"
                          aria-label="Select all visible"
                          title="Select all currently loaded rows"
                          // visually-checked when every loaded id is in `picked`
                          checked={
                            findingsItems.length > 0 &&
                            findingsItems.every((f) => picked.has(f.id))
                          }
                          onChange={(e) => {
                            if (e.target.checked) {
                              setPicked((cur) => {
                                const next = new Set(cur);
                                for (const f of findingsItems) next.add(f.id);
                                return next;
                              });
                            } else {
                              // Uncheck only the visible ones (preserve picks
                              // from earlier pages already loaded above this).
                              setPicked((cur) => {
                                const next = new Set(cur);
                                for (const f of findingsItems) next.delete(f.id);
                                return next;
                              });
                            }
                          }}
                        />
                      </th>
                      <th className="px-2 py-1">Path</th>
                      <th className="px-2 py-1 text-right">Size</th>
                      <th className="px-2 py-1">Reason</th>
                      {filterKind === "duplicate" && <th className="px-2 py-1">Existing</th>}
                      <th className="px-2 py-1 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {findingsItems.map((f) => (
                    <tr
                      key={f.id}
                      className={
                        "border-t border-zinc-800/60 " +
                        (picked.has(f.id) ? "bg-emerald-900/20" : "")
                      }
                    >
                      <td className="px-2 py-1">
                        <input
                          type="checkbox"
                          aria-label={`Select ${f.ingestPath}`}
                          checked={picked.has(f.id)}
                          disabled={f.status !== "pending"}
                          onChange={(e) => {
                            setPicked((cur) => {
                              const next = new Set(cur);
                              if (e.target.checked) next.add(f.id);
                              else next.delete(f.id);
                              return next;
                            });
                          }}
                        />
                      </td>
                      <td className="break-all px-2 py-1 font-mono text-zinc-300">{f.ingestPath}</td>
                      <td className="px-2 py-1 text-right text-zinc-400">
                        {formatBytes(f.sizeBytes)}
                      </td>
                      <td className="px-2 py-1 text-zinc-400">{f.reason ?? ""}</td>
                      {filterKind === "duplicate" && (
                        <td className="px-2 py-1">
                          {f.existingMediaId ? (
                            <a
                              href={`/media/${f.existingMediaId}`}
                              className="text-sky-400 underline"
                            >
                              {f.existingRelativePath ?? `#${f.existingMediaId}`}
                            </a>
                          ) : (
                            "—"
                          )}
                        </td>
                      )}
                      <td className="px-2 py-1 text-right">
                        {f.status === "pending" ? (
                          <div className="flex justify-end gap-1">
                            <button
                              onClick={() => resolveOne.mutate({ id: f.id, action: "delete" })}
                              disabled={resolveOne.isPending}
                              className="rounded bg-rose-800/60 px-2 py-0.5 text-rose-100 hover:bg-rose-800"
                            >
                              Delete source
                            </button>
                            <button
                              onClick={() => resolveOne.mutate({ id: f.id, action: "keep" })}
                              disabled={resolveOne.isPending}
                              className="rounded bg-zinc-700 px-2 py-0.5 hover:bg-zinc-600"
                            >
                              Keep
                            </button>
                          </div>
                        ) : (
                          <span className="text-zinc-500">{f.resolution}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                  </tbody>
                </table>
              </div>
              {findings.hasNextPage && (
                <div className="mt-3 flex justify-center">
                  <button
                    onClick={() => findings.fetchNextPage()}
                    disabled={findings.isFetchingNextPage}
                    className="rounded bg-zinc-800 px-4 py-1.5 text-xs hover:bg-zinc-700 disabled:opacity-50"
                  >
                    {findings.isFetchingNextPage
                      ? "Loading…"
                      : `Load next ${PAGE_SIZE} (${(findingsTotal - findingsItems.length).toLocaleString()} more)`}
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

function formatBytes(b: number): string {
  if (!Number.isFinite(b)) return "?";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
  return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`;
}
